import { FC, useEffect, useState } from "react";

import type { GeoJsonGeometry } from "@rubi/api-generated";
import { GOOGLE_MAP_ID, GoogleMapsProvider } from "@rubi/ui";
import { Map, useMap } from "@vis.gl/react-google-maps";

const MAP_INSTANCE_ID = "region-geometry-map";

/**
 * `bounds` is a GeoJSON Polygon envelope (a 5-point ring tracing the bounding
 * box). Pulled apart by hand rather than fed through `Data.addGeoJson` so we
 * get a `LatLngBounds` to call `fitBounds` with.
 */
function latLngBoundsFromEnvelope(
  envelope: GeoJsonGeometry,
): google.maps.LatLngBounds | null {
  const ring = (envelope as { coordinates?: unknown }).coordinates as
    | number[][][]
    | undefined;
  const points = ring?.[0];
  if (!points || points.length === 0) return null;
  const lngs = points.map((p) => p[0]);
  const lats = points.map((p) => p[1]);
  return new google.maps.LatLngBounds(
    { lat: Math.min(...lats), lng: Math.min(...lngs) },
    { lat: Math.max(...lats), lng: Math.max(...lngs) },
  );
}

export interface GeoJsonFeatureCollection {
  type: "FeatureCollection";
  features: object[];
}

const isFeatureCollection = (
  geoJson: GeoJsonGeometry | GeoJsonFeatureCollection,
): geoJson is GeoJsonFeatureCollection => geoJson.type === "FeatureCollection";

const DEFAULT_STYLE: google.maps.Data.StyleOptions = {
  fillColor: "#1f6feb",
  fillOpacity: 0.2,
  strokeColor: "#1f6feb",
  strokeWeight: 2,
};

const GeoJsonDataLayer: FC<{
  geoJson: GeoJsonGeometry | GeoJsonFeatureCollection;
  bounds?: GeoJsonGeometry | null;
  featureStyle?: google.maps.Data.StylingFunction;
  onFeatureClick?: (feature: google.maps.Data.Feature) => void;
  onFeatureHover?: (feature: google.maps.Data.Feature | null) => void;
}> = ({ geoJson, bounds, featureStyle, onFeatureClick, onFeatureHover }) => {
  const map = useMap(MAP_INSTANCE_ID);
  const [data, setData] = useState<google.maps.Data | null>(null);

  useEffect(() => {
    if (!map) return;

    const layer = new google.maps.Data({ map });
    layer.addGeoJson(
      isFeatureCollection(geoJson)
        ? geoJson
        : { type: "Feature", properties: {}, geometry: geoJson },
    );

    const envelopeBounds = bounds ? latLngBoundsFromEnvelope(bounds) : null;
    if (envelopeBounds) {
      map.fitBounds(envelopeBounds);
    } else {
      // No envelope given — derive a viewport from the geometry itself rather
      // than leaving the map at its default world view.
      const derived = new google.maps.LatLngBounds();
      layer.forEach((feature) =>
        feature
          .getGeometry()
          ?.forEachLatLng((latLng) => derived.extend(latLng)),
      );
      if (!derived.isEmpty()) map.fitBounds(derived);
    }

    setData(layer);
    return () => layer.setMap(null);
  }, [map, geoJson, bounds]);

  // Styling and listeners live in their own effects so a new callback identity
  // restyles / rebinds the existing layer instead of rebuilding it and
  // re-fitting the viewport.
  useEffect(() => {
    data?.setStyle(featureStyle ?? DEFAULT_STYLE);
  }, [data, featureStyle]);

  useEffect(() => {
    if (!data || !onFeatureClick) return;
    const listener = data.addListener(
      "click",
      (event: google.maps.Data.MouseEvent) => onFeatureClick(event.feature),
    );
    return () => listener.remove();
  }, [data, onFeatureClick]);

  useEffect(() => {
    if (!data || !onFeatureHover) return;
    const over = data.addListener(
      "mouseover",
      (event: google.maps.Data.MouseEvent) => {
        data.overrideStyle(event.feature, { strokeWeight: 4 });
        onFeatureHover(event.feature);
      },
    );
    const out = data.addListener(
      "mouseout",
      (event: google.maps.Data.MouseEvent) => {
        data.revertStyle(event.feature);
        onFeatureHover(null);
      },
    );
    return () => {
      over.remove();
      out.remove();
    };
  }, [data, onFeatureHover]);

  return null;
};

export interface RegionGeometryMapProps {
  /** A single geometry, or a FeatureCollection of several (e.g. `GET /region_map`). */
  geoJson: GeoJsonGeometry | GeoJsonFeatureCollection;
  /** GeoJSON Polygon envelope, for the initial viewport. Omit to fit the geometry itself. */
  bounds?: GeoJsonGeometry | null;
  height?: number | string;
  /** Per-feature style. Defaults to a uniform blue fill. Keep referentially stable. */
  featureStyle?: google.maps.Data.StylingFunction;
  /** Keep referentially stable (`useCallback`). */
  onFeatureClick?: (feature: google.maps.Data.Feature) => void;
  /** Called with the hovered feature, then `null` on mouseout. Keep referentially stable. */
  onFeatureHover?: (feature: google.maps.Data.Feature | null) => void;
}

/**
 * Renders GeoJSON — one geometry (a resolution tier of a Subregion or Region's
 * area) or a FeatureCollection (every active Region) — as shaded overlays.
 * Owns its own `GoogleMapsProvider` — safe to mount at each dev Regions page
 * per the provider's singleton-load contract.
 */
export const RegionGeometryMap: FC<RegionGeometryMapProps> = ({
  height = 360,
  ...layerProps
}) => (
  <GoogleMapsProvider>
    <Map
      id={MAP_INSTANCE_ID}
      mapId={GOOGLE_MAP_ID}
      defaultCenter={{ lat: 0, lng: 0 }}
      defaultZoom={2}
      gestureHandling="greedy"
      style={{ height, width: "100%" }}
    >
      <GeoJsonDataLayer {...layerProps} />
    </Map>
  </GoogleMapsProvider>
);
